// Edit→approve bind target for the auto-tailor round-trip. Written by the badge's
// Edit handler right before navigating to /resume/tailor/:versionId, read+cleared
// by ResumeTailor's approve. Deliberately NOT part of SearchDraft: Start clears
// that draft, and its shape-guard would drop a partial write. Single-use and
// version-scoped so a stale entry can never bind an unrelated approve.
export const PENDING_RESUME_BIND_KEY = 'beast.pendingResumeBind';

export interface PendingResumeBind {
  versionId: string; // the forked draft that will be approved
  searchId: string;  // the search to bind it to
}

export const setPendingResumeBind = (bind: PendingResumeBind): void => {
  sessionStorage.setItem(PENDING_RESUME_BIND_KEY, JSON.stringify(bind));
};

/** Non-consuming read: returns the stored searchId iff an entry matches
 *  `versionId`, WITHOUT clearing the slot (and without flushing a stale one).
 *  Used to decide the editor's "Back" destination — the searches list when the
 *  draft was forked from an existing search, the setup drawer otherwise — while
 *  leaving the bind intact so a later approve can still consume it. */
export const peekPendingResumeBind = (versionId: string): string | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_RESUME_BIND_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingResumeBind>;
    if (parsed?.versionId === versionId && typeof parsed.searchId === 'string') {
      return parsed.searchId;
    }
  } catch {
    /* storage unavailable or corrupt → treat as no pending bind */
  }
  return null;
};

/** Returns the searchId iff a stored entry matches `versionId`; always clears the
 *  slot (consume on match, flush stale on mismatch). */
export const takePendingResumeBind = (versionId: string): string | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_RESUME_BIND_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_RESUME_BIND_KEY);
    const parsed = JSON.parse(raw) as Partial<PendingResumeBind>;
    if (parsed?.versionId === versionId && typeof parsed.searchId === 'string') {
      return parsed.searchId;
    }
  } catch {
    /* storage unavailable or corrupt → treat as no pending bind */
  }
  return null;
};
